import { substituteBlockPlaceholders } from './blockPlaceholders';
interface EditorBlock {
  id: string;
  type: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  content: any;
  style: any;
  zIndex: number;
  visible?: boolean;
  locked?: boolean;
}

interface PageBackground {
  type: 'solid' | 'gradient' | 'split';
  // Solid color
  color?: string;
  // Gradient
  gradient?: {
    type: 'linear' | 'radial' | 'conic';
    angle?: number; // for linear gradients (0-360 degrees)
    stops: Array<{
      color: string;
      position: number; // 0-100%
    }>;
    // Radial gradient specific
    centerX?: number; // 0-100%
    centerY?: number; // 0-100%
    shape?: 'circle' | 'ellipse';
  };
  // Split background
  split?: {
    direction: 'horizontal' | 'vertical';
    position: number; // 0-100% where to split
    first: {
      type: 'solid' | 'gradient';
      color?: string;
      gradient?: PageBackground['gradient'];
    };
    second: {
      type: 'solid' | 'gradient';
      color?: string;
      gradient?: PageBackground['gradient'];
    };
  };
}

interface EditorPage {
  id: string;
  name: string;
  blocks: EditorBlock[];
  canvasSize: { width: number; height: number };
  background?: PageBackground;
}

interface EditorState {
  pages: EditorPage[];
  currentPageId: string;
  selectedBlockId: string | null;
  zoom: number;
  showGrid: boolean;
}

// Cache for fetched images to avoid duplicate fetches in one export session
const imageCache = new Map<string, string>();

/**
 * Computes a contrasting text color (white or black) based on background luminance.
 * Returns white for dark backgrounds, black for light backgrounds.
 */
function getContrastingTextColor(backgroundColor: string | undefined): string {
  if (!backgroundColor) return '#000000';

  // Remove # if present
  const hex = backgroundColor.replace('#', '');

  // Handle short hex codes (e.g., #fff -> #ffffff)
  const fullHex = hex.length === 3
    ? hex.split('').map(c => c + c).join('')
    : hex;

  // Convert to RGB
  const r = parseInt(fullHex.substring(0, 2), 16);
  const g = parseInt(fullHex.substring(2, 4), 16);
  const b = parseInt(fullHex.substring(4, 6), 16);

  // Check if parsing was successful
  if (isNaN(r) || isNaN(g) || isNaN(b)) return '#000000';

  // Calculate relative luminance (ITU-R BT.709)
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;

  // Return white for dark backgrounds (luminance < 0.5), black for light
  return luminance < 0.5 ? '#ffffff' : '#000000';
}

export class ExportService {
  // ─── HTML Escape Helper ────────────────────────────────────────────────────

  /**
   * Escapes HTML special characters to prevent code from being interpreted as HTML.
   * Critical for rendering code blocks containing HTML/JS/CSS.
   */
  private static escapeHtml(text: string): string {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  // ─── Image Helper ──────────────────────────────────────────────────────────

  /**
   * Fetches an image URL and converts it to a base64 data URL.
   * Returns the original URL on failure so the image still attempts to load.
   */
  static async fetchImageAsBase64(url: string): Promise<string> {
    if (!url) return '';
    // Already a data URL
    if (url.startsWith('data:')) return url;
    // Check cache
    if (imageCache.has(url)) return imageCache.get(url)!;

    try {
      const response = await fetch(url, { mode: 'cors' });
      if (!response.ok) return url;
      const blob = await response.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      imageCache.set(url, base64);
      return base64;
    } catch {
      // Return original URL as fallback — Puppeteer can still fetch it
      return url;
    }
  }

  /**
   * Pre-fetches all image URLs in the editor state and populates the cache.
   */
  private static async preloadImages(editorState: EditorState): Promise<void> {
    const imageUrls: string[] = [];

    for (const page of editorState.pages) {
      for (const block of page.blocks) {
        if (block.type === 'image' && block.content?.src) {
          imageUrls.push(block.content.src);
        }
      }
    }

    await Promise.all(imageUrls.map((url) => this.fetchImageAsBase64(url)));
  }

  // ─── Background Helpers ────────────────────────────────────────────────────

  private static getBackgroundStyle(background?: PageBackground): string {
    if (!background) return 'background-color: #ffffff;';

    switch (background.type) {
      case 'solid':
        return `background-color: ${background.color || '#ffffff'};`;

      case 'gradient': {
        if (!background.gradient) return 'background-color: #ffffff;';
        const { gradient } = background;
        const stops = gradient.stops
          .sort((a, b) => a.position - b.position)
          .map((stop) => `${stop.color} ${stop.position}%`)
          .join(', ');

        switch (gradient.type) {
          case 'linear':
            return `background: linear-gradient(${gradient.angle || 0}deg, ${stops});`;
          case 'radial': {
            const shape = gradient.shape || 'circle';
            const centerX = gradient.centerX || 50;
            const centerY = gradient.centerY || 50;
            return `background: radial-gradient(${shape} at ${centerX}% ${centerY}%, ${stops});`;
          }
          case 'conic': {
            const cCenterX = gradient.centerX || 50;
            const cCenterY = gradient.centerY || 50;
            const angle = gradient.angle || 0;
            return `background: conic-gradient(from ${angle}deg at ${cCenterX}% ${cCenterY}%, ${stops});`;
          }
          default:
            return 'background-color: #ffffff;';
        }
      }

      case 'split': {
        if (!background.split) return 'background-color: #ffffff;';
        const { split } = background;
        const { direction, position, first, second } = split;

        const firstBg =
          first.type === 'gradient' && first.gradient
            ? this.generateGradientCSS(first.gradient)
            : first.color || '#ffffff';
        const secondBg =
          second.type === 'gradient' && second.gradient
            ? this.generateGradientCSS(second.gradient)
            : second.color || '#ffffff';

        if (direction === 'horizontal') {
          return `background: linear-gradient(to bottom, ${firstBg} 0%, ${firstBg} ${position}%, ${secondBg} ${position}%, ${secondBg} 100%);`;
        } else {
          return `background: linear-gradient(to right, ${firstBg} 0%, ${firstBg} ${position}%, ${secondBg} ${position}%, ${secondBg} 100%);`;
        }
      }

      default:
        return 'background-color: #ffffff;';
    }
  }

  private static generateGradientCSS(gradient: PageBackground['gradient']): string {
    if (!gradient) return '#ffffff';

    const stops = gradient.stops
      .sort((a, b) => a.position - b.position)
      .map((stop) => `${stop.color} ${stop.position}%`)
      .join(', ');

    switch (gradient.type) {
      case 'linear':
        return `linear-gradient(${gradient.angle || 0}deg, ${stops})`;
      case 'radial': {
        const shape = gradient.shape || 'circle';
        const centerX = gradient.centerX || 50;
        const centerY = gradient.centerY || 50;
        return `radial-gradient(${shape} at ${centerX}% ${centerY}%, ${stops})`;
      }
      case 'conic': {
        const cCenterX = gradient.centerX || 50;
        const cCenterY = gradient.centerY || 50;
        const angle = gradient.angle || 0;
        return `conic-gradient(from ${angle}deg at ${cCenterX}% ${cCenterY}%, ${stops})`;
      }
      default:
        return '#ffffff';
    }
  }

  // ─── Main HTML Export ──────────────────────────────────────────────────────

  /**
   * Generates a fully self-contained HTML document from the editor state.
   * All images are embedded as base64 data URLs.
   * Safe to send to Puppeteer (no external resources needed).
   */
  static async exportAsHTML(editorState: EditorState, options?: { showPageNumbers?: boolean }): Promise<string> {
    const { pages } = editorState;
    const showPageNumbers = options?.showPageNumbers ?? false;
    const totalPages = pages.length;

    // Pre-fetch all images before rendering
    await this.preloadImages(editorState);

    // Build page HTML in parallel
    const pageHTMLs = await Promise.all(
      pages.map(async (page, pageIndex) => {
        const blocksHTML = await Promise.all(
          page.blocks
            .filter((block) => block.visible !== false && block.type !== 'section-marker')
            .sort((a, b) => a.zIndex - b.zIndex)
            .map((block) => this.renderBlockAsHTML(block))
        );
        const pageNumberHTML = showPageNumbers
          ? `<div style="position:absolute;bottom:10px;left:0;right:0;text-align:center;font-size:10px;color:#9ca3af;font-family:'Open Sans',Arial,sans-serif;pointer-events:none;z-index:9999;">${pageIndex + 1} / ${totalPages}</div>`
          : '';
        return `
    <div class="page-container" style="width: ${page.canvasSize.width}px; height: ${page.canvasSize.height}px; ${this.getBackgroundStyle(page.background)}">
        ${blocksHTML.join('')}
        ${pageNumberHTML}
    </div>`;
      })
    );

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Template Export</title>
    <!--
      ⚠️ THE DOCUMENT MUST CARRY ITS OWN FONT.

      The templates used to ask for "Segoe UI" first, but the HTML has never shipped a font
      file, so each renderer found whatever that machine happened to have:

        portal on Windows   -> Segoe UI
        portal on macOS     -> neither -> Helvetica/Arial
        headless Chromium   -> no Segoe UI installed -> Open Sans (loaded below)

      That is the whole of the "downloaded PDF looks different from the portal" report: same
      HTML, three different faces, because the font was assumed rather than supplied.

      Listing Open Sans only as a FALLBACK fixed macOS and left Windows broken, because a
      Windows reviewer still reached Segoe UI while the PDF could not. Open Sans is not
      metric-compatible with Segoe UI — advance widths differ, so a tight column wrapped one
      word earlier in the PDF than on screen, which is what "4 words on a line in the portal,
      3 in the PDF" was.

      So Segoe UI is no longer named anywhere in the proposal templates or here. Every
      renderer on every OS now resolves the same family, loaded from the same place, at the
      same weights (400/600/700 — kept in step with index.html, which loads Open Sans for the
      portal). A weight loaded on one side only would be synthesized on the other and the
      mismatch would creep back.

      Selawik — Microsoft's open, metric-identical replacement for Segoe UI — would let the
      deck go back to Segoe UI metrics exactly, if the Segoe UI look is ever wanted again. It
      has to be self-hosted, and it has to be added to index.html and here together.
    -->
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=block" rel="stylesheet">
    <style>

        * {
            box-sizing: border-box;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
            scrollbar-width: none !important;
            -ms-overflow-style: none !important;
        }
        *::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
        }
        @page {
            margin: 0;
            size: auto;
        }
        @media print {
            * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
                scrollbar-width: none !important;
            }
            html, body {
                margin: 0 !important;
                padding: 0 !important;
                background-color: white !important;
                overflow: hidden !important;
                width: 100% !important;
            }
            .page-container {
                page-break-after: always !important;
                page-break-inside: avoid !important;
                break-after: page !important;
                break-inside: avoid !important;
                margin: 0 !important;
                padding: 0 !important;
                box-shadow: none !important;
                overflow: hidden !important;
            }
            .page-container:last-child {
                page-break-after: auto !important;
                break-after: auto !important;
            }
            pre, code {
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
                white-space: pre-wrap !important;
                word-wrap: break-word !important;
                page-break-inside: avoid !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
            }
            pre {
                background-color: #f3f4f6 !important;
                border: 1px solid #e5e7eb !important;
                padding: 16px !important;
                border-radius: 6px !important;
            }
            code {
                font-family: Monaco, Consolas, 'Courier New', monospace !important;
                font-size: 13px !important;
                color: #1f2937 !important;
            }
            .script-block, .code-block, .script-block-preview {
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
                page-break-inside: avoid !important;
            }
            .script-block-preview * {
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
            }
        }
        html, body {
            margin: 0;
            padding: 0;
            background-color: #f5f5f5;
            font-family: 'Open Sans', Arial, sans-serif;
            overflow-x: hidden;
        }
        .page-container {
            position: relative;
            background-color: white;
            margin: 0 auto 20px auto;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            page-break-after: always !important;
            page-break-inside: avoid !important;
            break-after: page !important;
            break-inside: avoid !important;
            overflow: hidden !important;
            display: block;
            clear: both;
        }
        .page-container:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
        }
        .block {
            position: absolute;
            box-sizing: border-box;
            margin: 0;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }
        .image-block img {
            display: block;
        }
        .table-block table {
            border-collapse: collapse;
        }
        .table-block td, .table-block th {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        pre {
            margin: 0;
            white-space: pre-wrap;
            word-wrap: break-word;
            overflow-x: auto;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        code {
            font-family: Monaco, Consolas, 'Courier New', monospace;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        .script-block-preview {
            display: block;
            visibility: visible;
            opacity: 1;
        }
        .script-block-preview * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }
    </style>
</head>
<body>
    ${pageHTMLs.join('')}
</body>
</html>`;

    return html;
  }

  // ─── Block Renderer ────────────────────────────────────────────────────────

  private static async renderBlockAsHTML(block: EditorBlock): Promise<string> {
    const blockStyle = block.style || {};

    // Build the transform CSS if the block has rotation
    const rotation = blockStyle.rotation || blockStyle.transform;
    const transformCSS = rotation
      ? typeof rotation === 'number'
        ? `transform: rotate(${rotation}deg);`
        : `transform: ${rotation};`
      : '';

    const baseStyle = `
      position: absolute;
      left: ${block.position.x}px;
      top: ${block.position.y}px;
      width: ${block.size.width}px;
      height: ${block.size.height}px;
      z-index: ${block.zIndex};
      outline: none !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
      ${blockStyle.backgroundColor ? `background-color: ${blockStyle.backgroundColor};` : ''}
      ${blockStyle.color ? `color: ${blockStyle.color} !important;` : ''}
      ${blockStyle.borderRadius ? `border-radius: ${blockStyle.borderRadius};` : ''}
      ${blockStyle.padding ? `padding: ${blockStyle.padding};` : ''}
      ${blockStyle.margin ? `margin: ${blockStyle.margin};` : ''}
      ${blockStyle.opacity !== undefined ? `opacity: ${blockStyle.opacity};` : ''}
      ${transformCSS}
      ${blockStyle.textAlign ? `text-align: ${blockStyle.textAlign};` : ''}
      ${blockStyle.fontSize ? `font-size: ${blockStyle.fontSize};` : ''}
      ${blockStyle.fontWeight ? `font-weight: ${blockStyle.fontWeight};` : ''}
      ${blockStyle.fontFamily ? `font-family: ${blockStyle.fontFamily};` : ''}
      ${blockStyle.lineHeight ? `line-height: ${blockStyle.lineHeight};` : ''}
      ${blockStyle.letterSpacing ? `letter-spacing: ${blockStyle.letterSpacing};` : ''}
      ${blockStyle.boxShadow ? `box-shadow: ${blockStyle.boxShadow};` : ''}
      border: none;
    `.trim().replace(/\s+/g, ' ');

    switch (block.type) {
      // ── Text ──────────────────────────────────────────────────────────────
      case 'text': {
        const textStyle = block.textStyle || {};
        const richText = block.content?.richText || block.content?.text || 'Text';
        const explicitColor = textStyle.color || block.content?.color || blockStyle.color;
        const textColor = explicitColor || getContrastingTextColor(blockStyle.backgroundColor);
        const fontSize = textStyle.fontSize || block.content?.fontSize || blockStyle.fontSize || '16px';
        const fontWeight = textStyle.fontWeight || blockStyle.fontWeight || 'normal';
        const fontFamily = textStyle.fontFamily || blockStyle.fontFamily || 'inherit';
        const textAlign = textStyle.textAlign || blockStyle.textAlign || 'left';
        const lineHeight = textStyle.lineHeight || blockStyle.lineHeight || '1.4';
        const verticalAlign = textStyle.verticalAlign || block.content?.verticalAlign || 'center';

        const flexAlign =
          verticalAlign === 'top'
            ? 'flex-start'
            : verticalAlign === 'bottom'
            ? 'flex-end'
            : 'center';

        return `
          <div class="block" style="${baseStyle}
            font-size: ${fontSize};
            color: ${textColor};
            font-weight: ${fontWeight};
            font-family: ${fontFamily};
            text-align: ${textAlign};
            line-height: ${lineHeight};
            display: flex;
            align-items: ${flexAlign};
            justify-content: ${textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start'};
            overflow: hidden;
            word-wrap: break-word;
            padding: ${blockStyle.padding || '4px 8px'};
          ">
            <div style="width: 100%;">${richText}</div>
          </div>
        `;
      }

      // ── Image ─────────────────────────────────────────────────────────────
      case 'image': {
        if (!block.content?.src) return '';
        // Use base64 from cache (pre-loaded) or original URL
        const src = imageCache.get(block.content.src) ?? block.content.src;
        const objectFit = block.content?.objectFit || 'cover';
        const borderRadius = blockStyle.borderRadius || '0';
        return `
          <div class="block image-block" style="${baseStyle} overflow: hidden; border-radius: ${borderRadius};">
            <img src="${src}" alt="${block.content?.alt || ''}"
                 style="width: 100%; height: 100%; object-fit: ${objectFit}; display: block; border-radius: ${borderRadius};" />
          </div>
        `;
      }

      // ── Button ────────────────────────────────────────────────────────────
      case 'button': {
        const btnStyle = block.content?.style || 'primary';
        const btnBg =
          btnStyle === 'primary' ? (block.content?.backgroundColor || '#3b82f6')
          : btnStyle === 'secondary' ? (block.content?.backgroundColor || '#e5e7eb')
          : 'transparent';
        const btnColor =
          btnStyle === 'primary' ? '#ffffff'
          : btnStyle === 'secondary' ? '#374151'
          : (block.content?.color || '#374151');
        const btnBorder =
          btnStyle === 'outline' ? `1px solid ${block.content?.borderColor || '#d1d5db'}` : 'none';
        const fontSize = block.content?.size === 'small' ? '12px' : block.content?.size === 'large' ? '18px' : '14px';
        return `
          <div class="block" style="${baseStyle}
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${btnBg};
            color: ${btnColor};
            border: ${btnBorder};
            border-radius: ${blockStyle.borderRadius || '4px'};
            font-size: ${fontSize};
            font-weight: 500;
            cursor: pointer;
            text-decoration: none;
          ">
            ${block.content?.text || 'Button'}
          </div>
        `;
      }

      // ── Progress ──────────────────────────────────────────────────────────
      case 'progress': {
        const percentage = Math.min(100, Math.max(0, (block.content?.value || 0) / (block.content?.max || 100) * 100));
        const barColor = block.content?.color || '#3b82f6';
        return `
          <div class="block" style="${baseStyle} display: flex; align-items: center; padding: 8px;">
            <div style="flex: 1; height: 10px; background-color: #e5e7eb; border-radius: 9999px; overflow: hidden;">
              <div style="width: ${percentage}%; height: 100%; background-color: ${barColor};"></div>
            </div>
            ${block.content?.showLabel !== false ? `<span style="margin-left: 8px; font-size: 13px; font-weight: 500;">${Math.round(percentage)}%</span>` : ''}
          </div>
        `;
      }

      // ── List ──────────────────────────────────────────────────────────────
      case 'list': {
        const listType = block.content?.listType === 'numbered' ? 'ol' : 'ul';
        const items = (block.content?.items || []).map((item: string) => `<li>${item}</li>`).join('');
        return `
          <div class="block" style="${baseStyle} padding: 8px; overflow: hidden;">
            <${listType} style="margin: 0; padding-left: 20px;">${items}</${listType}>
          </div>
        `;
      }

      // ── Table ─────────────────────────────────────────────────────────────
      case 'table': {
        const tableContent = block.content || {};
        const tableRows = tableContent.rows || [];
        const tableColumns = tableContent.columns || [];
        const tableStyle = tableContent.tableStyle || {};
        const borderVal = `${tableStyle.borderWidth || 1}px ${tableStyle.borderStyle || 'solid'} ${tableStyle.borderColor || '#e5e7eb'}`;

        // Legacy format fallback
        if (tableRows.length === 0) {
          const legacyRows = tableContent.rows_legacy || tableContent.rows || 3;
          const legacyCols = tableContent.cols_legacy || tableContent.cols || 3;
          const legacyData = tableContent.data || {};
          const legacyCellStyles = tableContent.cellStyles || {};
          const legacyHeaderStyle = tableContent.headerStyle || {};
          const legacyHasHeader = tableContent.hasHeader || false;

          let html = `<table style="width: 100%; height: 100%; border-collapse: collapse; border: ${borderVal};">`;
          for (let r = 0; r < legacyRows; r++) {
            html += '<tr>';
            for (let c = 0; c < legacyCols; c++) {
              const cellKey = `${r}-${c}`;
              const cellData = typeof legacyData === 'object' && !Array.isArray(legacyData)
                ? (legacyData[cellKey] || '')
                : (Array.isArray(legacyData) ? (legacyData[r]?.[c] || '') : '');
              const isHeader = legacyHasHeader && r === 0;
              const cs = legacyCellStyles[cellKey] || {};
              const bg = cs.backgroundColor || (isHeader ? legacyHeaderStyle.backgroundColor || '#f3f4f6' : '#ffffff');
              const clr = cs.textColor || (isHeader ? legacyHeaderStyle.textColor || '#1f2937' : '#000000');
              const fw = cs.fontWeight || (isHeader ? legacyHeaderStyle.fontWeight || 'bold' : 'normal');
              const ta = cs.textAlign || (isHeader ? legacyHeaderStyle.textAlign || 'left' : 'left');
              const fs = `${cs.fontSize || (isHeader ? legacyHeaderStyle.fontSize || 14 : 14)}px`;
              html += `<td style="border: ${borderVal}; padding: 8px; background-color: ${bg} !important; color: ${clr}; text-align: ${ta}; font-weight: ${fw}; font-size: ${fs}; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;">${cellData}</td>`;
            }
            html += '</tr>';
          }
          html += '</table>';
          return `<div class="block table-block" style="${baseStyle}">${html}</div>`;
        }

        // Enhanced structure
        let html = `<table style="width: 100%; height: 100%; border-collapse: collapse; border: ${borderVal}; background-color: ${tableStyle.backgroundColor || 'white'};">`;
        tableRows.forEach((row: any, rowIndex: number) => {
          const rowStyle = row.style || {};
          html += '<tr>';
          row.cells.forEach((cell: any, cellIndex: number) => {
            const columnStyle = tableColumns[cellIndex]?.style || {};
            const cs = cell.style || {};
            const isHeader = tableContent.hasHeader && rowIndex === 0;
            const hs = tableContent.headerStyle || {};

            const bg = cs.backgroundColor || rowStyle.backgroundColor || columnStyle.backgroundColor || (isHeader ? hs.backgroundColor || '#f3f4f6' : 'white');
            const clr = cs.textColor || rowStyle.textColor || columnStyle.textColor || (isHeader ? hs.textColor || '#1f2937' : '#000000');
            const ta = cs.textAlign || rowStyle.textAlign || columnStyle.textAlign || (isHeader ? hs.textAlign || 'left' : 'left');
            const fw = cs.fontWeight || rowStyle.fontWeight || columnStyle.fontWeight || (isHeader ? hs.fontWeight || 'bold' : 'normal');
            const fs = `${cs.fontSize || rowStyle.fontSize || columnStyle.fontSize || (isHeader ? hs.fontSize || 14 : 14)}px`;

            html += `<td colspan="${cell.colspan || 1}" rowspan="${cell.rowspan || 1}"
              style="border: ${borderVal}; padding: 8px; background-color: ${bg} !important; color: ${clr}; text-align: ${ta}; font-weight: ${fw}; font-size: ${fs}; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;"
            >${cell.content || ''}</td>`;
          });
          html += '</tr>';
        });
        html += '</table>';
        return `<div class="block table-block" style="${baseStyle}">${html}</div>`;
      }

      // ── Placeholder ───────────────────────────────────────────────────────
      case 'placeholder':
        return `
          <div class="block" style="${baseStyle}
            background-color: #fef3c7;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 14px;
            font-weight: 500;
            padding: 8px;
          ">
            {{${block.content?.variable || 'variable'}}}
          </div>
        `;

      // ── Signature ─────────────────────────────────────────────────────────
      case 'signature':
        return `
          <div class="block" style="${baseStyle}
            display: flex;
            align-items: flex-end;
            justify-content: center;
            border-bottom: 2px solid #d1d5db;
            padding-bottom: 8px;
          ">
            <span style="font-size: 14px; color: #6b7280;">${block.content?.placeholder || 'Signature'}</span>
          </div>
        `;

      // ── DateTime ──────────────────────────────────────────────────────────
      case 'datetime': {
        const dateValue =
          block.content?.defaultValue === 'current'
            ? new Date().toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })
            : block.content?.format || 'MM/DD/YYYY';
        return `
          <div class="block" style="${baseStyle}
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: monospace;
            font-size: ${blockStyle.fontSize || '14px'};
            color: ${blockStyle.color || '#000000'};
          ">
            ${dateValue}
          </div>
        `;
      }

      // ── Divider ───────────────────────────────────────────────────────────
      case 'divider': {
        const isVertical = block.content?.orientation === 'vertical';
        const thickness = block.content?.thickness || 2;
        const color = block.content?.color || '#e5e7eb';
        return `
          <div class="block" style="${baseStyle} display: flex; align-items: center; justify-content: center;">
            <div style="
              width: ${isVertical ? thickness + 'px' : '100%'};
              height: ${isVertical ? '100%' : thickness + 'px'};
              background-color: ${color};
            "></div>
          </div>
        `;
      }

      // ── Video ─────────────────────────────────────────────────────────────
      case 'video':
        if (!block.content?.src) return '';
        return `
          <div class="block" style="${baseStyle} background-color: #000; overflow: hidden;">
            <video controls ${block.content?.poster ? `poster="${block.content.poster}"` : ''}
                   style="width: 100%; height: 100%; object-fit: contain;">
              <source src="${block.content.src}" type="video/mp4">
            </video>
          </div>
        `;

      // ── Chart (SVG-based, works in Puppeteer) ─────────────────────────────
      case 'chart': {
        const chartData = block.content?.data || [
          { label: 'A', value: 30 },
          { label: 'B', value: 50 },
          { label: 'C', value: 20 },
        ];
        const chartType = block.content?.chartType || 'bar';
        const colors = block.content?.colors || ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];
        const W = block.size.width;
        const H = block.size.height;

        if (chartType === 'bar') {
          const maxValue = Math.max(...chartData.map((d: any) => d.value), 1);
          const barCount = chartData.length;
          const availW = W - 40;
          const barW = Math.floor(availW / barCount) - 4;
          const chartH = H - 50;

          const bars = chartData.map((item: any, i: number) => {
            const barH = Math.round((item.value / maxValue) * chartH);
            const x = 20 + i * (barW + 4);
            const y = chartH - barH + 10;
            return `
              <rect x="${x}" y="${y}" width="${barW}" height="${barH}" fill="${colors[i % colors.length]}" />
              <text x="${x + barW / 2}" y="${H - 5}" text-anchor="middle" font-size="11" fill="#374151">${item.label}</text>
            `;
          }).join('');

          return `
            <div class="block" style="${baseStyle} background-color: white; overflow: hidden;">
              <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
                ${bars}
              </svg>
            </div>
          `;
        }

        if (chartType === 'pie') {
          const total = chartData.reduce((sum: number, d: any) => sum + d.value, 0) || 1;
          const cx = W / 2;
          const cy = H / 2;
          const r = Math.min(W, H) / 2 - 20;
          let currentAngle = -Math.PI / 2;

          const slices = chartData.map((item: any, i: number) => {
            const angle = (item.value / total) * 2 * Math.PI;
            const x1 = cx + r * Math.cos(currentAngle);
            const y1 = cy + r * Math.sin(currentAngle);
            const x2 = cx + r * Math.cos(currentAngle + angle);
            const y2 = cy + r * Math.sin(currentAngle + angle);
            const large = angle > Math.PI ? 1 : 0;
            currentAngle += angle;
            return `<path d="M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z" fill="${colors[i % colors.length]}" />`;
          }).join('');

          return `
            <div class="block" style="${baseStyle} background-color: white; overflow: hidden;">
              <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">${slices}</svg>
            </div>
          `;
        }

        // Line chart
        {
          const maxValue = Math.max(...chartData.map((d: any) => d.value), 1);
          const pts = chartData.map((item: any, i: number) => {
            const x = 20 + (i / (chartData.length - 1 || 1)) * (W - 40);
            const y = H - 30 - ((item.value / maxValue) * (H - 50));
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          }).join(' ');

          const dots = chartData.map((item: any, i: number) => {
            const x = 20 + (i / (chartData.length - 1 || 1)) * (W - 40);
            const y = H - 30 - ((item.value / maxValue) * (H - 50));
            return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4" fill="${colors[0]}" />
                    <text x="${x.toFixed(1)}" y="${H - 5}" text-anchor="middle" font-size="11" fill="#374151">${item.label}</text>`;
          }).join('');

          return `
            <div class="block" style="${baseStyle} background-color: white; overflow: hidden;">
              <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
                <polyline points="${pts}" fill="none" stroke="${colors[0]}" stroke-width="2" />
                ${dots}
              </svg>
            </div>
          `;
        }
      }

      // ── Advanced Block Types (from block.ts BlockType) ────────────────────
      case 'bar-chart':
      case 'line-chart':
      case 'pie-chart': {
        // These use the ChartData format with labels/datasets
        const chartContent = block.content as any;
        const chartData = chartContent?.data;
        const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
        const W = block.size.width;
        const H = block.size.height;

        if (!chartData?.labels || !chartData?.datasets?.length) {
          return `<div class="block" style="${baseStyle} background-color: #f9fafb; display: flex; align-items: center; justify-content: center; color: #9ca3af; font-size: 12px;">No chart data</div>`;
        }

        const labels: string[] = chartData.labels;
        const dataset = chartData.datasets[0];
        const values: number[] = dataset?.data || [];
        const maxValue = Math.max(...values, 1);

        if (block.type === 'pie-chart') {
          const total = values.reduce((a, b) => a + b, 0) || 1;
          const cx = W / 2, cy = H / 2, r = Math.min(W, H) / 2 - 20;
          let currentAngle = -Math.PI / 2;
          const slices = values.map((v, i) => {
            const angle = (v / total) * 2 * Math.PI;
            const x1 = cx + r * Math.cos(currentAngle);
            const y1 = cy + r * Math.sin(currentAngle);
            const x2 = cx + r * Math.cos(currentAngle + angle);
            const y2 = cy + r * Math.sin(currentAngle + angle);
            const large = angle > Math.PI ? 1 : 0;
            currentAngle += angle;
            return `<path d="M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z" fill="${colors[i % colors.length]}" />`;
          }).join('');
          return `<div class="block" style="${baseStyle} background-color: white; overflow: hidden;"><svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">${slices}</svg></div>`;
        }

        const barCount = values.length;
        const availW = W - 40;
        const barW = Math.max(8, Math.floor(availW / barCount) - 4);
        const chartH = H - 50;

        if (block.type === 'line-chart') {
          const pts = values.map((v, i) => {
            const x = 20 + (i / (barCount - 1 || 1)) * (W - 40);
            const y = H - 30 - ((v / maxValue) * chartH);
            return `${x.toFixed(1)},${y.toFixed(1)}`;
          }).join(' ');
          const dots = values.map((v, i) => {
            const x = 20 + (i / (barCount - 1 || 1)) * (W - 40);
            const y = H - 30 - ((v / maxValue) * chartH);
            return `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="4" fill="${colors[0]}" />
                    <text x="${x.toFixed(1)}" y="${H - 5}" text-anchor="middle" font-size="10" fill="#374151">${labels[i] || ''}</text>`;
          }).join('');
          return `<div class="block" style="${baseStyle} background-color: white; overflow: hidden;"><svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><polyline points="${pts}" fill="none" stroke="${colors[0]}" stroke-width="2" />${dots}</svg></div>`;
        }

        // bar-chart
        const bars = values.map((v, i) => {
          const barH = Math.round((v / maxValue) * chartH);
          const x = 20 + i * (barW + 4);
          const y = chartH - barH + 10;
          return `<rect x="${x}" y="${y}" width="${barW}" height="${barH}" fill="${colors[i % colors.length]}" />
                  <text x="${x + barW / 2}" y="${H - 5}" text-anchor="middle" font-size="10" fill="#374151">${labels[i] || ''}</text>`;
        }).join('');
        return `<div class="block" style="${baseStyle} background-color: white; overflow: hidden;"><svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">${bars}</svg></div>`;
      }

      // ── KPI Card ──────────────────────────────────────────────────────────
      case 'kpi-card': {
        const kpi = block.content as any;
        const trendColor = kpi?.trend === 'up' ? '#10b981' : kpi?.trend === 'down' ? '#ef4444' : '#6b7280';
        const trendSymbol = kpi?.trend === 'up' ? '↑' : kpi?.trend === 'down' ? '↓' : '→';
        return `
          <div class="block" style="${baseStyle}
            background-color: ${blockStyle.backgroundColor || '#ffffff'} !important;
            border-radius: ${blockStyle.borderRadius || '8px'};
            border: ${blockStyle.border || '1px solid #e5e7eb'};
            padding: 16px;
            display: flex;
            flex-direction: column;
            justify-content: center;
          ">
            <div style="font-size: 12px; color: #6b7280; margin-bottom: 4px;">${kpi?.title || 'KPI'}</div>
            <div style="font-size: 28px; font-weight: 700; color: ${kpi?.color || '#1f2937'};">${kpi?.value ?? '—'}</div>
            ${kpi?.subtitle ? `<div style="font-size: 12px; color: #6b7280; margin-top: 4px;">${kpi.subtitle}</div>` : ''}
            ${kpi?.trendValue ? `<div style="font-size: 12px; color: ${trendColor}; margin-top: 4px;">${trendSymbol} ${kpi.trendValue}</div>` : ''}
          </div>
        `;
      }

      // ── Progress Bar (advanced type) ──────────────────────────────────────
      case 'progress-bar': {
        const pb = block.content as any;
        const pct = Math.min(100, Math.max(0, ((pb?.value || 0) / (pb?.max || 100)) * 100));
        const barColor = pb?.color || '#3b82f6';
        const barH = pb?.height || 10;
        return `
          <div class="block" style="${baseStyle} display: flex; flex-direction: column; justify-content: center; padding: 8px;">
            ${pb?.label ? `<div style="font-size: 13px; margin-bottom: 6px;">${pb.label}</div>` : ''}
            <div style="width: 100%; height: ${barH}px; background-color: #e5e7eb; border-radius: ${barH / 2}px; overflow: hidden;">
              <div style="width: ${pct}%; height: 100%; background-color: ${barColor};"></div>
            </div>
            ${pb?.showPercentage !== false ? `<div style="font-size: 12px; color: #6b7280; margin-top: 4px;">${Math.round(pct)}%</div>` : ''}
          </div>
        `;
      }

      // ── Gauge Chart ───────────────────────────────────────────────────────
      case 'gauge-chart': {
        const gc = block.content as any;
        const value = gc?.value ?? 0;
        const min = gc?.min ?? 0;
        const max = gc?.max ?? 100;
        const pct = (value - min) / (max - min);
        const W = block.size.width;
        const H = block.size.height;
        const cx = W / 2;
        const cy = H * 0.7;
        const r = Math.min(W / 2, H * 0.7) - 15;
        const startAngle = Math.PI;
        const endAngle = 2 * Math.PI;
        const angle = startAngle + pct * Math.PI;

        const trackX1 = (cx + r * Math.cos(startAngle)).toFixed(2);
        const trackY1 = (cy + r * Math.sin(startAngle)).toFixed(2);
        const trackX2 = (cx + r * Math.cos(endAngle)).toFixed(2);
        const trackY2 = (cy + r * Math.sin(endAngle)).toFixed(2);
        const valueX2 = (cx + r * Math.cos(angle)).toFixed(2);
        const valueY2 = (cy + r * Math.sin(angle)).toFixed(2);
        const valueLarge = pct > 0.5 ? 1 : 0;

        return `
          <div class="block" style="${baseStyle} background-color: ${blockStyle.backgroundColor || 'white'} !important; display: flex; flex-direction: column; align-items: center; justify-content: center;">
            <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
              <path d="M ${trackX1} ${trackY1} A ${r} ${r} 0 0 1 ${trackX2} ${trackY2}"
                    fill="none" stroke="#e5e7eb" stroke-width="12" stroke-linecap="round" />
              <path d="M ${(cx + r * Math.cos(startAngle)).toFixed(2)} ${(cy + r * Math.sin(startAngle)).toFixed(2)} A ${r} ${r} 0 ${valueLarge} 1 ${valueX2} ${valueY2}"
                    fill="none" stroke="${gc?.color || '#3b82f6'}" stroke-width="12" stroke-linecap="round" />
              <text x="${cx}" y="${cy - 5}" text-anchor="middle" font-size="20" font-weight="bold" fill="#1f2937">${value}${gc?.unit || ''}</text>
              ${gc?.title ? `<text x="${cx}" y="${cy + 20}" text-anchor="middle" font-size="12" fill="#6b7280">${gc.title}</text>` : ''}
            </svg>
          </div>
        `;
      }

      // ── Data Table ────────────────────────────────────────────────────────
      case 'data-table': {
        const dt = block.content as any;
        const headers: string[] = dt?.headers || [];
        const rows: string[][] = dt?.rows || [];
        const tableStyle = dt?.tableStyle || {};
        const headerStyle = dt?.headerStyle || {};
        const borderVal = `${tableStyle.borderWidth || 1}px solid ${tableStyle.borderColor || '#e5e7eb'}`;

        let html = `<table style="width: 100%; border-collapse: collapse; border: ${borderVal}; font-size: 13px;">`;
        if (headers.length > 0) {
          html += '<thead><tr>';
          headers.forEach((h, i) => {
            const colStyle = dt?.columnStyles?.[i] || {};
            html += `<th style="border: ${borderVal}; padding: 8px; background-color: ${headerStyle.backgroundColor || '#f3f4f6'} !important; color: ${headerStyle.textColor || '#1f2937'}; font-weight: ${headerStyle.fontWeight || 'bold'}; text-align: ${colStyle.textAlign || headerStyle.textAlign || 'left'}; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;">${h}</th>`;
          });
          html += '</tr></thead>';
        }
        html += '<tbody>';
        rows.forEach((row, ri) => {
          const rowStyle = dt?.rowStyles?.[ri] || {};
          const isStripe = dt?.striped && ri % 2 === 1;
          const rowBg = rowStyle.backgroundColor || (isStripe ? '#f9fafb' : 'white');
          html += '<tr>';
          row.forEach((cell, ci) => {
            const cellStyle = dt?.cellStyles?.[`${ri}-${ci}`] || {};
            const colStyle = dt?.columnStyles?.[ci] || {};
            const bg = cellStyle.backgroundColor || rowBg;
            const clr = cellStyle.textColor || rowStyle.textColor || colStyle.textColor || '#000000';
            const ta = cellStyle.textAlign || colStyle.textAlign || rowStyle.textAlign || 'left';
            html += `<td style="border: ${borderVal}; padding: 8px; background-color: ${bg} !important; color: ${clr}; text-align: ${ta}; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;">${cell}</td>`;
          });
          html += '</tr>';
        });
        html += '</tbody></table>';

        return `<div class="block" style="${baseStyle} overflow: hidden;">${html}</div>`;
      }

      // ── Badge ─────────────────────────────────────────────────────────────
      case 'badge': {
        const badge = block.content as any;
        const variantColors: Record<string, [string, string]> = {
          primary:   ['#dbeafe', '#1d4ed8'],
          secondary: ['#f3f4f6', '#374151'],
          success:   ['#d1fae5', '#065f46'],
          warning:   ['#fef3c7', '#92400e'],
          danger:    ['#fee2e2', '#991b1b'],
          info:      ['#e0f2fe', '#0369a1'],
        };
        const [bg, clr] = variantColors[badge?.variant || 'primary'];
        const fontSize = badge?.size === 'small' ? '11px' : badge?.size === 'large' ? '16px' : '13px';
        return `
          <div class="block" style="${baseStyle}
            display: inline-flex;
            align-items: center;
            justify-content: center;
            background-color: ${bg};
            color: ${clr};
            border-radius: ${badge?.rounded !== false ? '9999px' : '4px'};
            padding: 4px 10px;
            font-size: ${fontSize};
            font-weight: 600;
          ">
            ${badge?.text || 'Badge'}
          </div>
        `;
      }

      // ── Quote ─────────────────────────────────────────────────────────────
      case 'quote': {
        const quote = block.content as any;
        return `
          <div class="block" style="${baseStyle}
            border-left: 4px solid ${blockStyle.borderColor || '#3b82f6'};
            padding: 12px 16px;
            background-color: ${blockStyle.backgroundColor || '#f8fafc'};
          ">
            <p style="margin: 0; font-size: ${blockStyle.fontSize || '15px'}; color: ${blockStyle.color || '#1f2937'}; font-style: italic; line-height: 1.6;">"${quote?.text || ''}"</p>
            ${quote?.author ? `<footer style="margin-top: 8px; font-size: 13px; color: #6b7280;">— ${quote.author}${quote.citation ? `, ${quote.citation}` : ''}</footer>` : ''}
          </div>
        `;
      }

      // ── Callout ───────────────────────────────────────────────────────────
      case 'callout': {
        const callout = block.content as any;
        const typeColors: Record<string, [string, string, string]> = {
          info:    ['#dbeafe', '#1d4ed8', 'ℹ'],
          warning: ['#fef3c7', '#92400e', '⚠'],
          success: ['#d1fae5', '#065f46', '✓'],
          error:   ['#fee2e2', '#991b1b', '✕'],
          note:    ['#f3f4f6', '#374151', '📝'],
        };
        const [bg, clr, icon] = typeColors[callout?.type || 'info'];
        return `
          <div class="block" style="${baseStyle}
            background-color: ${bg};
            border-radius: 6px;
            padding: 12px 16px;
            display: flex;
            gap: 10px;
            align-items: flex-start;
          ">
            <span style="font-size: 16px; flex-shrink: 0; margin-top: 1px;">${icon}</span>
            <div>
              ${callout?.title ? `<div style="font-weight: 600; font-size: 14px; color: ${clr}; margin-bottom: 4px;">${callout.title}</div>` : ''}
              <div style="font-size: 13px; color: ${clr}; line-height: 1.5;">${callout?.text || ''}</div>
            </div>
          </div>
        `;
      }

      // ── Gradient Background ───────────────────────────────────────────────
      case 'gradient-bg': {
        const gb = block.content as any;
        const dirMap: Record<string, string> = {
          'to-right': '90deg', 'to-left': '270deg', 'to-top': '0deg',
          'to-bottom': '180deg', 'to-top-right': '45deg', 'to-bottom-right': '135deg',
        };
        const dir = dirMap[gb?.direction || 'to-right'];
        const gradColors = (gb?.colors || ['#3b82f6', '#8b5cf6']).join(', ');
        const opacity = gb?.opacity ?? 1;
        return `
          <div class="block" style="${baseStyle}
            background: linear-gradient(${dir}, ${gradColors});
            opacity: ${opacity};
          "></div>
        `;
      }

      // ── Border Frame ──────────────────────────────────────────────────────
      case 'border-frame': {
        const bf = block.content as any;
        return `
          <div class="block" style="${baseStyle}
            border: ${bf?.borderWidth || 2}px ${bf?.borderStyle || 'solid'} ${bf?.borderColor || '#000000'};
            border-radius: ${bf?.borderRadius || 0}px;
            padding: ${bf?.padding || 0}px;
          "></div>
        `;
      }

      // ── Timeline ──────────────────────────────────────────────────────────
      case 'timeline': {
        const tl = block.content as any;
        const items = tl?.items || [];
        const statusColors: Record<string, string> = {
          completed: '#10b981', current: '#3b82f6', upcoming: '#d1d5db',
        };
        const itemsHTML = items.map((item: any) => {
          const dotColor = statusColors[item.status || 'upcoming'];
          return `
            <div style="display: flex; gap: 12px; margin-bottom: 12px;">
              <div style="display: flex; flex-direction: column; align-items: center;">
                <div style="width: 12px; height: 12px; border-radius: 50%; background-color: ${dotColor}; flex-shrink: 0;"></div>
                <div style="width: 2px; flex: 1; background-color: #e5e7eb; margin-top: 4px;"></div>
              </div>
              <div style="flex: 1; padding-bottom: 8px;">
                <div style="font-size: 14px; font-weight: 600; color: #1f2937;">${item.title || ''}</div>
                ${item.date ? `<div style="font-size: 12px; color: #6b7280; margin-top: 2px;">${item.date}</div>` : ''}
                ${item.description ? `<div style="font-size: 13px; color: #4b5563; margin-top: 4px;">${item.description}</div>` : ''}
              </div>
            </div>
          `;
        }).join('');
        return `
          <div class="block" style="${baseStyle} padding: 8px; overflow: hidden;">
            ${itemsHTML}
          </div>
        `;
      }

      // ── Accordion ─────────────────────────────────────────────────────────
      case 'accordion': {
        const ac = block.content as any;
        const items = ac?.items || [];
        const itemsHTML = items.map((item: any) => `
          <div style="border-bottom: 1px solid #e5e7eb; padding: 10px 0;">
            <div style="font-size: 14px; font-weight: 600; color: #1f2937; margin-bottom: ${item.isOpen ? '8px' : '0'};">${item.title || ''}</div>
            ${item.isOpen ? `<div style="font-size: 13px; color: #4b5563; line-height: 1.5;">${item.content || ''}</div>` : ''}
          </div>
        `).join('');
        return `
          <div class="block" style="${baseStyle} padding: 8px; overflow: hidden;">
            ${itemsHTML}
          </div>
        `;
      }

      // ── Icon Block ────────────────────────────────────────────────────────
      case 'icon-block': {
        const ib = block.content as any;
        const iconSize = ib?.size || 32;
        return `
          <div class="block" style="${baseStyle}
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <div style="
              width: ${iconSize}px;
              height: ${iconSize}px;
              background-color: ${ib?.color || '#3b82f6'};
              border-radius: ${ib?.style === 'outline' ? '0' : '50%'};
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-size: ${Math.round(iconSize * 0.6)}px;
            ">●</div>
          </div>
        `;
      }

      // ── Icon (legacy 'icon' type) ─────────────────────────────────────────
      case 'icon':
        return `
          <div class="block" style="${baseStyle}
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <div style="
              width: ${block.content?.size || 48}px;
              height: ${block.content?.size || 48}px;
              background-color: ${block.content?.color || '#3b82f6'};
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-weight: bold;
              font-size: 24px;
            ">★</div>
          </div>
        `;

      // ── QR Code ───────────────────────────────────────────────────────────
      case 'qrcode':
        return `
          <div class="block" style="${baseStyle}
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: white;
          ">
            <div style="
              width: 80px;
              height: 80px;
              background-color: #000;
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-size: 12px;
            ">QR</div>
          </div>
        `;

      // ── Shape ─────────────────────────────────────────────────────────────
      case 'shape': {
        const shapeType = block.content?.shapeType || block.content?.shape || 'rectangle';
        const fillColor = block.content?.fillColor || block.content?.color || blockStyle.backgroundColor || '#3b82f6';
        const strokeColor = block.content?.strokeColor || blockStyle.borderColor || '#000000';
        const strokeWidth = block.content?.strokeWidth || 0;

        switch (shapeType) {
          case 'circle':
            return `<div class="block" style="${baseStyle} background-color: ${fillColor} !important; border-radius: 50%; border: ${strokeWidth}px solid ${strokeColor};"></div>`;
          case 'triangle':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points="50,10 90,90 10,90" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          case 'line':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><line x1="0" y1="50" x2="100" y2="50" stroke="${fillColor}" stroke-width="${Math.max(strokeWidth, 2)}" /></svg></div>`;
          case 'arrow-right':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points="10,30 70,30 70,20 90,50 70,80 70,70 10,70" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          case 'arrow-left':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points="90,30 30,30 30,20 10,50 30,80 30,70 90,70" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          case 'arrow-up':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points="30,90 30,30 20,30 50,10 80,30 70,30 70,90" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          case 'arrow-down':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points="30,10 30,70 20,70 50,90 80,70 70,70 70,10" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          case 'star':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points="50,10 61,35 90,35 69,54 79,85 50,70 21,85 31,54 10,35 39,35" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          case 'diamond':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points="50,10 90,50 50,90 10,50" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          case 'hexagon':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points="25,25 75,25 90,50 75,75 25,75 10,50" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          case 'heart':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><path d="M50,85 C50,85 20,65 20,45 C20,35 25,25 35,25 C40,25 45,30 50,35 C55,30 60,25 65,25 C75,25 80,35 80,45 C80,65 50,85 50,85 Z" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          case 'cross':
            return `<div class="block" style="${baseStyle} background-color: transparent;"><svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none"><polygon points="40,10 60,10 60,40 90,40 90,60 60,60 60,90 40,90 40,60 10,60 10,40 40,40" fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" /></svg></div>`;
          default: // rectangle
            return `<div class="block" style="${baseStyle} background-color: ${fillColor} !important; border: ${strokeWidth}px solid ${strokeColor};"></div>`;
        }
      }

      // ── Script Block (HTML/CSS/JS) - Render Preview ──────────────────────
      case 'script': {
        // Resolved, not raw — the portal substitutes these and the download did not, so the
        // broker's details reached the client's PDF unfilled. See substituteBlockPlaceholders.
        const html = substituteBlockPlaceholders(
          block.content?.html || '',
          block.content?.placeholderValues,
        );
        const css = block.content?.css || '';
        const javascript = block.content?.javascript || '';

        // Generate preview content (rendered HTML with CSS applied)
        const hasContent = html || css || javascript;

        if (!hasContent) {
          return `
            <div class="block script-block" style="${baseStyle}
              background-color: #f9fafb !important;
              border-radius: 6px;
              padding: 0;
              display: flex;
              align-items: center;
              justify-content: center;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            ">
              <div style="color: #9ca3af; text-align: center; font-size: 14px;">Empty script block</div>
            </div>
          `;
        }

        // Render the HTML content with CSS applied inline
        const previewContent = `
          <div class="block script-block-preview" style="${baseStyle}
            background-color: white !important;
            border-radius: 6px;
            padding: 0;
            overflow: hidden;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          ">
            ${css ? `<style>${css}</style>` : ''}
            ${html || '<div style="text-align: center; color: #999; font-size: 14px;">No HTML content</div>'}
            ${javascript ? `<div style="margin-top: 12px; padding: 8px; background-color: #fef3c7; border-left: 4px solid #f59e0b; font-size: 11px; color: #92400e;">
              <strong>Note:</strong> JavaScript cannot be executed in PDF. Interactive features are disabled.
            </div>` : ''}
          </div>
        `;

        return previewContent;
      }

      // ── Code Block ────────────────────────────────────────────────────────
      case 'code': {
        const codeContent = block.content?.text || block.content?.code || '';
        const language = block.content?.language || '';
        const bgColor = blockStyle.backgroundColor || '#f3f4f6';
        const textColor = blockStyle.color || '#1f2937';
        // CRITICAL: Escape HTML entities to prevent code from being interpreted as HTML
        const escapedCode = ExportService.escapeHtml(codeContent);
        return `
          <div class="block code-block" style="${baseStyle}
            background-color: ${bgColor} !important;
            border-radius: 6px;
            padding: 16px;
            overflow-x: auto;
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          ">
            ${language ? `<div class="code-language" style="font-size: 11px; color: #6b7280; margin-bottom: 8px; font-weight: 600; text-transform: uppercase; font-family: Monaco, Consolas, monospace; display: block !important; visibility: visible !important;">${language}</div>` : ''}
            <pre style="margin: 0; white-space: pre-wrap; word-wrap: break-word; font-family: Monaco, Consolas, 'Courier New', monospace; font-size: ${blockStyle.fontSize || '13px'}; color: ${textColor} !important; line-height: 1.5; display: block !important; visibility: visible !important; background-color: transparent !important;"><code style="font-family: Monaco, Consolas, 'Courier New', monospace; color: ${textColor} !important; display: inline !important; visibility: visible !important;">${escapedCode}</code></pre>
          </div>
        `;
      }

      // ── Headings (legacy) ─────────────────────────────────────────────────
      case 'heading1':
      case 'heading2':
      case 'heading3': {
        const explicitColor = block.content?.color || blockStyle.color;
        const headingColor = explicitColor || getContrastingTextColor(blockStyle.backgroundColor);
        return `
          <div class="block" style="${baseStyle}
            font-size: ${block.content?.fontSize || (block.type === 'heading1' ? 32 : block.type === 'heading2' ? 24 : 18)}px;
            font-weight: ${block.content?.fontWeight || 'bold'};
            color: ${headingColor};
            text-align: ${block.content?.textAlign || blockStyle.textAlign || 'left'};
            display: flex;
            align-items: center;
            padding: 8px;
            overflow: hidden;
          ">
            ${block.content?.text || 'Heading'}
          </div>
        `;
      }

      case 'paragraph': {
        const explicitColor = block.content?.color || blockStyle.color;
        const paragraphColor = explicitColor || getContrastingTextColor(blockStyle.backgroundColor);
        return `
          <div class="block" style="${baseStyle}
            font-size: ${block.content?.fontSize || 14}px;
            font-weight: ${block.content?.fontWeight || 'normal'};
            color: ${paragraphColor};
            text-align: ${block.content?.textAlign || blockStyle.textAlign || 'left'};
            line-height: ${block.content?.lineHeight || 1.6};
            padding: 8px;
            overflow: hidden;
          ">
            ${block.content?.text || ''}
          </div>
        `;
      }

      case 'variable':
        return `
          <div class="block" style="${baseStyle}
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #fef3c7;
            border: 2px dashed #f59e0b;
            border-radius: 4px;
            font-weight: 500;
          ">
            ${block.content?.defaultValue || block.content?.placeholder || 'Variable'}
          </div>
        `;

      // Section markers are editor-only organisational labels — never rendered in exports
      case 'section-marker':
        return '';

      default:
        return '';
    }
  }

  // ─── Legacy Sync Method ────────────────────────────────────────────────────

  /**
   * @deprecated Use exportAsHTML (async) instead.
   * Kept for backwards compatibility with existing callers.
   */
  static exportAsHTMLSync(editorState: EditorState): string {
    const { pages } = editorState;
    // Synchronous fallback — images won't be base64 encoded
    const pageHTMLs = pages.map((page) => {
      const blocksHTML = page.blocks
        .filter((b) => b.visible !== false && b.type !== 'section-marker')
        .sort((a, b) => a.zIndex - b.zIndex)
        .map((b) => {
          // Simple sync render (images remain as original URLs)
          const style = b.style || {};
          const base = `position:absolute;left:${b.position.x}px;top:${b.position.y}px;width:${b.size.width}px;height:${b.size.height}px;z-index:${b.zIndex};${style.backgroundColor ? `background-color:${style.backgroundColor};` : ''}`;
          if (b.type === 'image' && b.content?.src) {
            return `<div style="${base}overflow:hidden;"><img src="${b.content.src}" style="width:100%;height:100%;object-fit:${b.content?.objectFit || 'cover'};" /></div>`;
          }
          if (b.type === 'text') {
            const richText = b.content?.richText || b.content?.text || '';
            return `<div style="${base}display:flex;align-items:center;padding:4px 8px;font-size:${style.fontSize || '14px'};color:${style.color || '#000'};font-family:${style.fontFamily || 'inherit'};">${richText}</div>`;
          }
          return `<div style="${base}"></div>`;
        })
        .join('');
      return `<div style="position:relative;width:${page.canvasSize.width}px;height:${page.canvasSize.height}px;${this.getBackgroundStyle(page.background)}overflow:hidden;">${blocksHTML}</div>`;
    });
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>*{-webkit-print-color-adjust:exact!important;}body{margin:0;padding:0;}</style></head><body>${pageHTMLs.join('')}</body></html>`;
  }

  // ─── JSON Export ──────────────────────────────────────────────────────────

  static exportAsJSON(editorState: EditorState): string {
    return JSON.stringify(editorState, null, 2);
  }

  // ─── File Download Helper ─────────────────────────────────────────────────

  static downloadFile(content: string, filename: string, mimeType = 'text/plain') {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  // ─── Legacy Compat ────────────────────────────────────────────────────────

  static exportForPrint(editorState: EditorState): Promise<string> {
    return this.exportAsHTML(editorState);
  }

  static async exportToHTML(blocks: unknown[], canvasState: any, _options: unknown): Promise<string> {
    const editorState: EditorState = {
      pages: [{
        id: 'page-1',
        name: 'Page 1',
        blocks: blocks as EditorBlock[],
        canvasSize: { width: canvasState.width || 800, height: canvasState.height || 600 },
        background: canvasState.background || { type: 'solid', color: '#ffffff' },
      }],
      currentPageId: 'page-1',
      selectedBlockId: null,
      zoom: 1,
      showGrid: false,
    };
    return this.exportAsHTML(editorState);
  }

  static exportLegacyToHTML(blocks: unknown[], canvasState: any, _options: unknown): string {
    const editorState: EditorState = {
      pages: [{
        id: 'page-1',
        name: 'Page 1',
        blocks: blocks as EditorBlock[],
        canvasSize: { width: canvasState.width || 800, height: canvasState.height || 600 },
        background: canvasState.background || { type: 'solid', color: '#ffffff' },
      }],
      currentPageId: 'page-1',
      selectedBlockId: null,
      zoom: 1,
      showGrid: false,
    };
    return this.exportAsHTMLSync(editorState);
  }
}
